import React, { useState, useEffect } from 'react';
import { NavigationMenu } from '../components/NavigationMenu';
import { Button } from '../components/Button';
import { LoadingAnimation } from '../components/LoadingAnimation';
import { useGeneration } from '../context/GenerationContext';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useAuth } from '../context/AuthContext';
import { Users, ArrowRight, Type, HelpCircle } from 'lucide-react';
import { audienceApi, tasksApi } from '../services/api';
import { localStorageService } from '../services/localStorageService';
import { PageHeader } from '../components/PageHeader';
import { useGenerationStatus } from '../hooks/useGenerationStatus';
import { GenerationLimitsStatus } from '../components/GenerationLimitsStatus';

// Интерфейс для типизации черновика
interface AudienceDraft {
  projectDescription: string;
  audienceName: string;
  questionAnswers: Record<string, string>;
}

function Audience() {
  useDocumentTitle('Анализ целевой аудитории');
  const [projectDescription, setProjectDescription] = useState('');
  const [audienceName, setAudienceName] = useState('');
  const { startGeneration, canAddTask } = useGeneration();
  const navigate = useCustomNavigate();
  const { token, hasActiveSubscription } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [questions, setQuestions] = useState<string[]>([]);
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>({});
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [taskStartTime, setTaskStartTime] = useState<number | null>(null);
  const [taskStatusCheckCount, setTaskStatusCheckCount] = useState(0);
  const { status: generationStatus, loading: isLimitsLoading, error: limitsError, refetch: refetchGenerationStatus } = useGenerationStatus('AudienceAnalysis');

  // Загрузка черновика при монтировании компонента
  useEffect(() => {
    const savedDraft = localStorageService.getAudienceDraft<AudienceDraft>();
    if (savedDraft) {
      // Используем проверки на наличие данных для предотвращения установки пустых значений
      if (savedDraft.projectDescription) setProjectDescription(savedDraft.projectDescription);
      if (savedDraft.audienceName) setAudienceName(savedDraft.audienceName);
      if (savedDraft.questionAnswers && Object.keys(savedDraft.questionAnswers).length > 0) {
        setQuestionAnswers(savedDraft.questionAnswers);
      }
    }
  }, []);

  // Сохранение черновика при изменении состояний
  useEffect(() => {
    // Используем debounce для сохранения черновика, чтобы не перегружать localStorage
    const saveTimeout = setTimeout(() => {
      const draftData: AudienceDraft = {
        projectDescription,
        audienceName,
        questionAnswers
      };
      
      localStorageService.saveAudienceDraft(draftData);
    }, 500); // задержка в 500 мс для дебаунсинга
    
    return () => {
      clearTimeout(saveTimeout);
    };
  }, [projectDescription, audienceName, questionAnswers]);

  // Функция для очистки черновика после успешного старта задачи
  const clearAudienceDraft = () => {
    localStorageService.clearAudienceDraft();
  };

  // Отслеживание изменения currentTaskId
  useEffect(() => {
    console.log('[Debug] Значение currentTaskId в Audience изменилось:', currentTaskId);
  }, [currentTaskId]);

  // Функция для проверки статуса задачи
  useEffect(() => {
    if (!currentTaskId) return;

    console.log(`[Debug] Отслеживание начато для задачи: ${currentTaskId}`);

    // Не проверяем статус временных задач
    if (currentTaskId.startsWith('temp-')) {
      console.log(`[Debug] Пропускаем проверку статуса для временного ID: ${currentTaskId}`);
      return;
    }

    // Устанавливаем время начала задачи
    if (!taskStartTime) {
      setTaskStartTime(Date.now());
    }

    const checkTaskStatus = async () => {
      try {
        console.log(`[Debug] Проверка статуса задачи: ${currentTaskId}`);
        
        // Увеличиваем счетчик проверок
        setTaskStatusCheckCount(prev => prev + 1);
        
        // Проверяем, не слишком ли долго выполняется задача (более 15 минут)
        if (taskStartTime && (Date.now() - taskStartTime > 15 * 60 * 1000)) {
          console.log(`[Debug] Задача ${currentTaskId} выполняется более 15 минут. Перенаправляем на главную страницу.`);
          // Не показываем ошибку, просто перенаправляем на главную
          navigate('/');
          setCurrentTaskId(null);
          setTaskStartTime(null);
          setTaskStatusCheckCount(0);
          setIsLoading(false);
          return;
        }
        
        // Проверяем, не слишком ли много проверок без изменения статуса (более 100)
        if (taskStatusCheckCount > 100) {
          console.log(`[Debug] Слишком много проверок статуса для задачи ${currentTaskId}. Прекращаем отслеживание.`);
          setCurrentTaskId(null);
          setTaskStartTime(null);
          setTaskStatusCheckCount(0);
          setIsLoading(false);
          return;
        }
        
        // Используем метод tasksApi.getTaskResult для проверки статуса
        const response = await tasksApi.getTaskResult(currentTaskId);
        const task = response.data;
        
        console.log(`[Debug] Получен статус задачи: ${task.status}`);
        
        // Проверяем, завершена ли задача
        if (task.status === 'Successfull' || task.status === 'Successful') {
          // Дополнительно проверяем наличие данных результата
          if (task.data) {
            console.log('[Debug] Данные задачи получены:', task.data);
            
            const redirectUrl = `/audience/result/${currentTaskId}`;
            console.log(`[Debug] Задача завершена, перенаправление на: ${redirectUrl}`);
            
            // Очищаем текущую задачу перед перенаправлением
            setCurrentTaskId(null);
            setTaskStartTime(null);
            setTaskStatusCheckCount(0);
            setIsLoading(false);
            
            // Добавляем задержку перед перенаправлением, чтобы данные точно успели обработаться
            setTimeout(() => {
              try {
                console.log(`[Debug] Выполняем перенаправление на: ${redirectUrl}`);
                
                // Сохраняем ID задачи в sessionStorage для гарантированного доступа на странице результатов
                sessionStorage.setItem('lastAudienceTaskId', currentTaskId || '');
                
                // Используем прямое перенаправление
                window.location.href = `${window.location.origin}${redirectUrl}`;
              } catch (err) {
                console.error('[Debug] Ошибка при перенаправлении:', err);
                
                // Запасной вариант - просто открыть новую вкладку
                window.open(`${window.location.origin}${redirectUrl}`, '_blank');
              }
            }, 500);
          } else {
            console.error('[Debug] Задача завершена, но данные пустые');
            
            // Очищаем текущую задачу
            setCurrentTaskId(null);
            setTaskStartTime(null);
            setTaskStatusCheckCount(0);
            setIsLoading(false);
          }
        } else if (task.status === 'Error' || task.status === 'Cancelled') {
          // Если ошибка или отмена, тоже очищаем задачу
          console.log(`[Debug] Задача не выполнена (статус: ${task.status}), прекращаем отслеживание`);
          
          if (task.status === 'Error') {
          }
          
          setCurrentTaskId(null);
          setTaskStartTime(null);
          setTaskStatusCheckCount(0);
          setIsLoading(false);
        } else {
          console.log(`[Debug] Задача еще выполняется (статус: ${task.status}), продолжаем отслеживание`);
        }
      } catch (error) {
        console.error('[Debug] Ошибка при проверке статуса задачи:', error);
        
        // Если это 404 ошибка (задача не найдена), возможно задача еще создается
        if (error && typeof error === 'object' && 'response' in error) {
          const response = (error as any).response;
          if (response?.status === 404) {
            console.log(`[Debug] Задача ${currentTaskId} еще не найдена на сервере, продолжаем ожидание...`);
            return; // Продолжаем polling
          }
        }
        
        // Если произошло слишком много ошибок проверки, останавливаем отслеживание
        if (taskStatusCheckCount > 20) {
          console.log('[Debug] Слишком много ошибок при проверке статуса. Прекращаем отслеживание.');
          setCurrentTaskId(null);
          setTaskStartTime(null);
          setTaskStatusCheckCount(0);
          setIsLoading(false);
        }
      }
    };

    // Проверяем статус каждые 3 секунды
    const intervalId = setInterval(checkTaskStatus, 3000);
    console.log(`[Debug] Установлен интервал ${intervalId} для проверки статуса`);

    // Очищаем интервал при размонтировании компонента
    return () => {
      console.log(`[Debug] Очистка интервала ${intervalId}`);
      clearInterval(intervalId);
    };
  }, [currentTaskId, navigate, taskStartTime, taskStatusCheckCount]);

  // Обработка ответов на вопросы
  const handleAnswerChange = (question: string, answer: string) => {
    setQuestionAnswers(prev => ({
      ...prev,
      [question]: answer
    }));
  };

  // Функция для получения уточняющих вопросов
  const handleGetQuestions = async () => {
    if (!token) {
      navigate('/auth/login');
      return;
    }

    if (generationStatus && !generationStatus.canGenerate) {
      return;
    }

    // Проверяем наличие активной подписки
    if (!hasActiveSubscription) {
      navigate('/tariffs');
      return;
    }

    if (!projectDescription.trim()) {
      return;
    }

    if (!audienceName.trim()) {
      return;
    }

    setIsLoadingQuestions(true);

    try {
      console.log('[Debug] Отправка запроса на получение уточняющих вопросов');
      
      // Формируем запрос
      const requestData = {
        projectDescription,
        audienceName
      };
      
      const response = await audienceApi.getQuestions(requestData);

      const data = response.data;
      console.log('[Debug] Ответ с вопросами:', data);

      // Проверяем, является ли ответ массивом (прямой массив вопросов)
      if (Array.isArray(data)) {
        console.log('[Debug] Получен прямой массив вопросов');
        setQuestions(data);
        
        // Если вопросов нет, сразу начинаем анализ
        if (data.length === 0) {
          await startAnalysis();
        }
      } 
      // Проверяем, если ответ - объект с ключом questions
      else if (data.questions && Array.isArray(data.questions)) {
        console.log('[Debug] Получен объект с массивом вопросов в поле questions');
        setQuestions(data.questions);
        
        // Если вопросов нет, сразу начинаем анализ
        if (data.questions.length === 0) {
          await startAnalysis();
        }
      } else {
      }
    } catch (error) {
      console.error('Error getting questions:', error);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  // Функция для запуска анализа
  const startAnalysis = async () => {
    if (!token) {
      navigate('/auth/login');
      return;
    }

    if (generationStatus && !generationStatus.canGenerate) {
      return;
    }

    // Проверяем наличие активной подписки
    if (!hasActiveSubscription) {
      navigate('/tariffs');
      return;
    }

    if (!projectDescription.trim() || !audienceName.trim()) {
      return;
    }

    if (!canAddTask) {
      return;
    }

    // Показываем анимацию сразу
    setIsLoading(true);
    
    // Очищаем черновик сразу
    clearAudienceDraft();

    try {
      console.log('[Debug] Отправка запроса на анализ целевой аудитории');
      
      // Объединяем основные данные с ответами на вопросы
      const requestData = {
        projectDescription,
        audienceName,
        answers: questionAnswers
      };
      
      // Отправляем запрос на сервер (не ждем ответа)
      audienceApi.analyze(requestData).then((response) => {
        const data = response.data;
        console.log('[Debug] Полный ответ от сервера:', data);
        
        // Поиск идентификатора задачи в ответе
        const realTaskId = data.taskId || data.id || (data.task && data.task.id);
        
        if (realTaskId) {
          console.log(`[Debug] Получен реальный taskId: ${realTaskId}`);
          // Задача будет автоматически добавлена через SignalR уведомления от бэкенда
          // startGeneration убран, чтобы избежать дублирования
        }
      }).catch((error) => {
        console.error('Error analyzing target audience:', error);
        // Ошибки обрабатываются в фоне, не влияют на UI
      });
      
      // Показываем анимацию 4 секунды, затем переходим на страницу задач
      setTimeout(() => {
        setIsLoading(false);
        navigate('/tasks');
      }, 4000);
      
    } catch (error) {
      console.error('Error starting analysis:', error);
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleGetQuestions();
  };

  const handleSubmitWithAnswers = async (e: React.FormEvent) => {
    e.preventDefault();
    await startAnalysis();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
        <div className="hidden md:block">
          <NavigationMenu />
        </div>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-96px)]">
          <div className="scale-125 transform">
            <LoadingAnimation size="large" text="Анализ целевой аудитории..." />
          </div>
          <div className="mt-16 max-w-2xl text-center">
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
              Анализ целевой аудитории может занять некоторое время.
              <br /><br />
              Ваша задача обрабатывается в фоновом режиме. Вы можете перейти на другую страницу, а результат будет доступен в разделе "Задачи".
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 mobile-content-padding overflow-x-hidden">
      <div className="hidden md:block">
        <NavigationMenu />
      </div>
      <div className="w-full max-w-7xl mx-auto px-4 py-8 lg:py-12">
        <PageHeader
          title="Анализ целевой аудитории"
          subtitle="Опишите ваш проект для детального анализа и сегментации целевой аудитории"
        />

        <div className="w-full max-w-3xl mx-auto">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-4 sm:p-6 md:p-8 overflow-hidden">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-6">Описание вашего проекта</h2>
            
            {questions.length === 0 ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                    <Type className="w-4 h-4 mr-2" />
                    Название целевой аудитории
                  </label>
                  <input
                    type="text"
                    value={audienceName}
                    onChange={(e) => setAudienceName(e.target.value)}
                    className="w-full p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors"
                    placeholder="Например: Целевая аудитория для мобильного приложения FitTrack"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    Описание проекта
                  </label>
                  <textarea
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    className="w-full p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors"
                    rows={4}
                    placeholder="Опишите ваш проект, продукт или услугу, для которых нужен анализ целевой аудитории..."
                    required
                  />
                </div>
                
                <GenerationLimitsStatus 
                  className="mb-4" 
                  generationType="AudienceAnalysis" 
                  status={generationStatus}
                  loading={isLimitsLoading}
                  error={limitsError}
                />

                <Button
                  type="submit"
                  disabled={isLoadingQuestions || !generationStatus?.canGenerate}
                  variant="primary"
                  fullWidth
                  className="flex items-center justify-center py-3 sm:py-3 px-4 sm:px-6 font-medium text-base sm:text-lg shadow-md hover:shadow-lg transform"
                >
                  <span>{isLoadingQuestions ? 'Анализирую...' : 'Начать анализ'}</span>
                  {!isLoadingQuestions && <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />}
                </Button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-500" />
                    Уточняющие вопросы
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-6">
                    Пожалуйста, ответьте на несколько вопросов, чтобы мы могли лучше понять вашу целевую аудиторию
                  </p>
                  <p className="text-amber-500 dark:text-amber-400 mb-4 text-xs sm:text-sm italic">
                    * Поля не обязательны
                  </p>
                  
                  <form onSubmit={handleSubmitWithAnswers} className="space-y-6">
                    {questions.map((question, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {index + 1}. {question}
                        </label>
                        <textarea
                          value={questionAnswers[question] || ''}
                          onChange={(e) => handleAnswerChange(question, e.target.value)}
                          className="w-full p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors min-h-[80px] overflow-hidden"
                          style={{ resize: 'vertical' }}
                          placeholder="Ваш ответ..."
                          onInput={(e) => {
                            const target = e.target as HTMLTextAreaElement;
                            target.style.height = 'auto';
                            target.style.height = `${target.scrollHeight}px`;
                          }}
                        />
                      </div>
                    ))}
                    
                    <GenerationLimitsStatus 
                      className="mb-4" 
                      generationType="AudienceAnalysis" 
                      status={generationStatus}
                      loading={isLimitsLoading}
                      error={limitsError}
                    />
                    
                    <Button
                      type="submit"
                      disabled={!generationStatus?.canGenerate}
                      variant="primary"
                      fullWidth
                      className="flex items-center justify-center py-3 sm:py-3 px-4 sm:px-6 font-medium text-base sm:text-lg shadow-md hover:shadow-lg transform"
                    >
                      <span>Продолжить анализ</span>
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                    </Button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Audience; 